import infoButtonPopupTemplate from './info-button-popup.html?raw';

export class InfoButtonPopup extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.isInfoVisible = false;

        this.boundHandleClickOutside = this.handleOutsideClick.bind(this);
        this.handleCloseByKey = this.handleCloseByKey.bind(this);
    }

    static get observedAttributes() {
        return ['info-text', 'position', 'button-label'];
    }

    connectedCallback() {
        this.render();
    }

    attributeChangedCallback(_, oldValue, newValue) {
        if (oldValue !== newValue) {
            this.render();
        }
    }

    render() {
        this.clearPreviousRender();
        this.createHtmlTemplate();
        this.addStyles();
        this.decorateElements();
    }

    clearPreviousRender() {
        if (!this.shadowRoot) {
            this.attachShadow({ mode: 'open' });
        } else {
            this.shadowRoot.innerHTML = '';
        }
    }

    createHtmlTemplate() {
        const template = document.createElement('template');
        template.innerHTML = infoButtonPopupTemplate;
        this.shadowRoot.appendChild(template.content.cloneNode(true));
    }

    get infoContainer() {
        return this.shadowRoot.querySelector('.info-container');
    }

    get infoButton() {
        return this.shadowRoot.querySelector('.info-button');
    }

    get infoButtonLabel() {
        return this.shadowRoot.querySelector('.info-button-label');
    }

    get infoText() {
        return this.getAttribute('info-text') || '';
    }

    get iconSize() {
        return this.getAttribute('icon-size') || 'default';
    }

    get buttonLabelText() {
        return this.getAttribute('button-label') || 'information';
    }

    get isHoverable() {
        return (
            this.hasAttribute('hoverable') &&
            this.getAttribute('hoverable') === 'true'
        );
    }
    get position() {
        return this.getAttribute('position') || 'bottom';
    }

    addStyles() {
        const style = document.createElement('style');
        style.textContent = `
            .icon {
                width: ${this.getIconSizeCss()};
                height: ${this.getIconSizeCss()};
            }
        `;

        this.shadowRoot.appendChild(style);
    }

    decorateElements() {
        this.infoContainer.setAttribute('position', this.position);
        this.infoButtonLabel.innerText = this.buttonLabelText;

        const labelId = 'label-' + Math.random().toString(36).slice(2, 10);
        this.infoButtonLabel.id = labelId;
        this.infoButton.setAttribute('aria-labelledby', labelId);

        this.addButtonOnClick();

        if (this.isHoverable) {
            this.addHoverFunctionality();
        }
    }

    addButtonOnClick() {
        this.infoButton.addEventListener('click', () => {
            if (this.isInfoVisible) {
                this.isInfoVisible = false;
                this.hideInfo();
                return;
            }

            this.isInfoVisible = true;
            this.showInfo();
        });
    }

    addHoverFunctionality() {
        this.infoButton.addEventListener('mouseenter', () => {
            if (!this.isInfoVisible) {
                this.showInfo();
            }
        });

        this.infoButton.addEventListener('mouseleave', () => {
            if (!this.isInfoVisible) {
                this.hideInfo();
            }
        });
    }

    handleOutsideClick = (event) => {
        if (!this.contains(event.target)) {
            this.hideInfo();
        }
    };

    handleCloseByKey(event) {
        if (
            event.key === 'Escape' ||
            event.key === 'Esc' ||
            event.key === 'Tab'
        ) {
            this.hideInfo();
        }
    }

    showInfo() {
        this.infoContainer.style.opacity = 0;
        this.infoContainer.textContent = '';
        this.infoContainer.style.display = 'block';

        // timeout for triggering text changes for aria-live property
        setTimeout(() => {
            this.infoContainer.textContent = this.infoText;
            this.infoContainer.style.opacity = 1;
        }, 100);

        this.keepInfoContainerInViewport();

        document.addEventListener('click', this.handleOutsideClick);
        document.addEventListener('keydown', this.handleCloseByKey);
    }

    hideInfo() {
        this.isInfoVisible = false;
        this.infoContainer.style.display = 'none';
        this.infoContainer.textContent = this.infoText;
        this.infoContainer.style.opacity = 0;

        document.removeEventListener('click', this.handleOutsideClick);
        document.removeEventListener('keydown', this.handleCloseByKey);
    }

    keepInfoContainerInViewport() {
        const viewportWidth = window.outerWidth;
        const viewportHeight = window.outerHeight;
        const position = this.infoContainer.getAttribute('position');
        const documentPadding = 8;

        if (position === 'top' || position === 'bottom') {
            this.infoContainer.style.transform = 'translateX(-50%)';
        }

        let rect = this.infoContainer.getBoundingClientRect();
        let deltaX = 0;

        if (position === 'top' || position === 'bottom') {
            if (rect.left < documentPadding) {
                deltaX = documentPadding - rect.left;
            } else if (rect.right > viewportWidth - documentPadding) {
                deltaX = viewportWidth - documentPadding - rect.right;
            }
        }

        if (position === 'left' || position === 'right') {
            if (
                rect.left < documentPadding ||
                rect.right > viewportWidth - documentPadding
            ) {
                this.infoContainer.setAttribute('position', 'bottom');
                this.infoContainer.style.transform = 'translateX(-50%)';
                rect = this.infoContainer.getBoundingClientRect();

                if (rect.left < documentPadding) {
                    deltaX = documentPadding - rect.left;
                } else if (rect.right > viewportWidth - documentPadding) {
                    deltaX = viewportWidth - documentPadding - rect.right - 24;
                }
            }
        }

        if (position === 'top') {
            if (rect.top < documentPadding) {
                this.infoContainer.setAttribute('position', 'bottom');
                rect = this.infoContainer.getBoundingClientRect();
            }
        }

        if (position === 'bottom') {
            if (rect.bottom > viewportHeight - documentPadding) {
                this.infoContainer.setAttribute('position', 'top');
                rect = this.infoContainer.getBoundingClientRect();
            }
        }

        if (deltaX !== 0) {
            this.infoContainer.style.transform = `translateX(calc(-50% + ${deltaX}px))`;
        } else {
            if (
                position === 'top' ||
                position === 'bottom' ||
                this.infoContainer.getAttribute('position') === 'bottom' ||
                this.infoContainer.getAttribute('position') === 'top'
            ) {
                this.infoContainer.style.transform = 'translateX(-50%)';
            }
        }
    }

    getIconSizeCss() {
        switch (this.iconSize) {
            case 'xlarge':
                return '2rem';
            case 'large':
                return '1.5rem';
            case 'medium':
                return '1.25rem';
            case 'small':
                return '1rem';
            case 'xsmall':
                return '0.875rem';
            default:
                return '1.5rem';
        }
    }
}

customElements.define('svea-info-button-popup', InfoButtonPopup);
